// Modules
import { ErrorHandler, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CdkModule } from '@app/cdk.module';
import { RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { DevExpressModule } from '@app/devexpress.module';

// Pipes
import { CoreTemplateCastPipe } from '@common/pipes/core-template-cast.pipe';
import { CoreSafeHtmlPipe } from '@app/standalones/core-safe-html.pipe';
import { CoreFormatArrayJoinPipe, CoreFormatCurrencyPipe, CoreFormatDateTimePipe, CoreFormatDayOfWeekPipe, CoreFormatPercentualNumberPipe, CoreFormatTruncatePipe } from './pipes/core-format.pipe';
import { CoreSortPipe } from './pipes/core-sort.pipe';

// Directives
import { EidosCapsLockDirective } from '@common/directives/eidos-caps-lock.directive';

// Custom adapters and handlers
import { CoreErrorHandler } from '@common/core-error-handler.class';

////////////////// Components

//// Dialogs
import { EidosChangePasswordDialogComponent } from '@common/components/eidos-dialogs/eidos-change-password-dialog/eidos-change-password-dialog.component';
import { EidosSettingsDialogComponent } from '@common/components/eidos-dialogs/eidos-settings-dialog/eidos-settings-dialog.component';
import { EidosImpersonateUserDialogComponent } from '@common/components/eidos-dialogs/eidos-impersonate-user-dialog/eidos-impersonate-user-dialog.component';
import { EidosAlertDialogComponent } from '@common/components/eidos-dialogs/eidos-alert-dialog/eidos-alert-dialog.component';
import { EidosResetCredentialsDialogComponent } from '@common/components/eidos-dialogs/eidos-reset-credentials-dialog/eidos-reset-credentials-dialog.component';
import { EidosErrorDialogComponent } from '@common/components/dialogs/eidos-error-dialog/eidos-error-dialog.component';
import { EidosConfirmationDialogComponent } from './components/eidos-dialogs/eidos-confirmation-dialog/eidos-confirmation-dialog.component';

//// Inputs
import { EidosInputDateTimeComponent } from '@common/components/entities/inputs/eidos-input-datetime/eidos-input-datetime.component';
import { EidosInputBooleanComponent } from '@common/components/entities/inputs/eidos-input-boolean/eidos-input-boolean.component';
import { EidosInputNumberComponent } from '@common/components/entities/inputs/eidos-input-number/eidos-input-number.component';
import { EidosInputCheckboxComponent } from '@common/components/entities/inputs/eidos-input-checkbox/eidos-input-checkbox.component';
import { EidosInputSwitchComponent } from '@common/components/entities/inputs/eidos-input-switch/eidos-input-switch.component';
import { EidosInputSelectizeComponent } from '@common/components/entities/inputs/eidos-input-selectize/eidos-input-selectize.component';
import { EidosInputDateTimeRangeComponent } from './components/entities/inputs/eidos-input-datetimerange/eidos-input-datetimerange.component';
import { EidosInputNumberPopupComponent } from './components/entities/inputs/eidos-input-number-popup/eidos-input-number-popup.component';
import { EidosInputPhoneComponent } from './components/entities/inputs/eidos-input-phone/eidos-input-phone.component';
import { EidosUploadFileComponent } from './components/entities/inputs/eidos-upload-file/eidos-upload-file.component';
import { EidosInputEditorComponent } from './components/entities/inputs/eidos-input-editor/eidos-input-editor.component';

//// Filters
import { EidosFilterFormComponent } from '@common/components/eidos-filter-form/eidos-filter-form.component';
import { EidosFilterFormCommandsComponent } from '@common/components/eidos-filter-form/eidos-filter-form-commands/eidos-filter-form-commands.component';
import { EidosFilterFormShowMoreComponent } from '@common/components/eidos-filter-form/eidos-filter-form-show-more/eidos-filter-form-show-more.component';
import { EidosFilterComponent } from '@common/components/entities/filters/eidos-filter/eidos-filter.component';
import { EidosFilterDaterangeComponent } from '@common/components/entities/filters/eidos-filter-daterange/eidos-filter-daterange.component';
import { EidosFilterCheckboxComponent } from '@common/components/entities/filters/eidos-filter-checkbox/eidos-filter-checkbox.component';
import { EidosFilterSelectizeComponent } from '@common/components/entities/filters/eidos-filter-selectize/eidos-filter-selectize.component';
import { EidosFilterTextComponent } from './components/entities/filters/eidos-filter-text/eidos-filter-text.component';
import { EidosFilterNumberPopupComponent } from './components/entities/filters/eidos-filter-number-popup/eidos-filter-number-popup.component';
import { EidosFilterNumberComponent } from './components/entities/filters/eidos-filter-number/eidos-filter-number.component';

//// Pages
import { EidosMainPageComponent } from '@common/pages/eidos-main-page/eidos-main-page.component';
import { EidosLoginComponent } from '@common/pages/eidos-login/eidos-login.component';
import { EidosResetCredentialsComponent } from '@common/pages/eidos-reset-credentials/eidos-reset-credentials.component';
import { EidosHomePageComponent } from '@common/pages/eidos-home-page/eidos-home-page.component';

import { EidosTopbarComponent } from '@common/components/eidos-topbar/eidos-topbar.component';
import { EidosIconComponent } from '@common/components/eidos-icon/eidos-icon.component';
import { EidosProgressSpinnerComponent } from '@common/components/eidos-progress-spinner/eidos-progress-spinner.component';
import { EidosUserComponent } from '@common/components/eidos-user/eidos-user.component';
import { EidosUserAvatarComponent } from '@common/components/eidos-user-avatar/eidos-user-avatar.component';
import { EidosActionMenuComponent } from '@common/components/eidos-action-menu/eidos-action-menu.component';
import { EidosConfigurationViewerComponent } from '@common/pages/eidos-configuration-viewer/eidos-configuration-viewer.component';
import { EidosConfigurationItemViewerComponent } from '@common/pages/eidos-configuration-viewer/eidos-configuration-item-viewer/eidos-configuration-item-viewer.component';

import { EidosLoginTokenComponent } from '@common/pages/eidos-login-token/eidos-login-token.component';
import { EidosExternalAppContainerComponent } from '@common/eidos-external-app/eidos-external-app-container.component';
import { EidosMatrixComponent } from '@common/components/eidos-matrix/eidos-matrix.component';
import { EidosGridComponent } from '@common/components/eidos-grid/eidos-grid.component';
import { EidosGridButtonCellTemplateComponent } from './components/eidos-grid/eidos-grid-button-cell-template/eidos-grid-button-cell-template.component';
import { EidosGridLinkCellTemplateComponent } from './components/eidos-grid/eidos-grid-link-cell-template/eidos-grid-link-cell-template.component';
import { EidosFilterCategoryComponent } from './components/entities/filters/eidos-filter-category/eidos-filter-category.component';
import { EidosLightFormComponent } from './components/eidos-light-form/eidos-light-form.component';
import { EidosGridFullTextTemplateComponent } from './components/eidos-grid/eidos-grid-full-text-template/eidos-grid-full-text-template.component';
import { EidosGraphicChooserComponent } from './components/entities/objects/eidos-graphic-chooser/eidos-graphic-chooser.component';
import { EidosCommandPanelComponent } from './components/entities/objects/eidos-command-panel/eidos-command-panel.component';
import { EidosDragDropDirective } from './directives/eidos-drag-drop.directive';
import { EidosModalComponent } from './components/eidos-modal/eidos-modal.component';
import { EidosModalTitleComponent } from './components/eidos-modal/eidos-modal-title/eidos-modal-title.component';
import { EidosButtonComponent } from './components/eidos-button/eidos-button.component';
import { EidosDateSelectorGridComponent } from './components/eidos-date-selector-grid/eidos-date-selector-grid.component';
import { EidosTargetValueDirective } from './directives/eidos-target-value.directive';
import { EidosExpandableTextComponent } from './components/eidos-expandable-text/eidos-expandable-text.component';
import { AngularEditorModule } from '@kolkov/angular-editor';
import { CorePopupListSelectorComponent } from './components/core-popup-list-selector/core-popup-list-selector.component';
import { CorePopupListSelectorModalComponent } from './components/core-popup-list-selector/core-popup-list-selector-modal/core-popup-list-selector-modal.component';
import { ToJSDatePipe } from './pipes/to-jsdate.pipe';
import { FormatDurationPipe } from './pipes/format-duration.pipe';
import { TooltipDrawerDirective } from "@app/standalones/directives/tooltip-drawer.directive";
import { CoreModalContainerComponent } from './components/core-modal-container/core-modal-container.component';
import { CoreModalItemComponent } from './components/core-modal-container/core-modal-item/core-modal-item.component';
import { CoreFormModalComponent } from './components/core-modals/core-form-modal/core-form-modal.component';
import { CoreGridModalComponent } from './components/core-modals/core-grid-modal/core-grid-modal.component';
import { CoreTextModalComponent } from './components/core-modals/core-text-modal/core-text-modal.component';
import { CoreTemplateModalComponent } from './components/core-modals/core-template-modal/core-template-modal.component';
import { LbaActionMenuComponent } from '@app/standalones/ui/components/lba-action-menu/lba-action-menu.component';
import { EidosCountryStateCountyAddressComponent } from './components/eidos-country-state-county-address/eidos-country-state-county-address.component';
import { EidosAddressAutocompleteComponent } from './components/eidos-address-autocomplete/eidos-address-autocomplete.component';
import { CoreIframeComponent } from './components/core-modals/core-iframe/core-iframe.component';


/**
 * Main core application
 * It includes components and utils to reuse in every custom module
 *
 * @export
 * @class CoreModule
 */
@NgModule({
  declarations: [
    EidosMainPageComponent,
    EidosTopbarComponent,
    EidosExternalAppContainerComponent,
    EidosLoginComponent,
    EidosResetCredentialsComponent,
    EidosHomePageComponent,
    EidosIconComponent,
    EidosProgressSpinnerComponent,
    EidosUserComponent,
    EidosUserAvatarComponent,
    EidosActionMenuComponent,

    EidosGridComponent,
    EidosMatrixComponent,
    EidosGridButtonCellTemplateComponent,
    EidosGridLinkCellTemplateComponent,

    EidosFilterFormComponent,
    EidosFilterFormCommandsComponent,
    EidosFilterFormShowMoreComponent,
    EidosFilterComponent,
    EidosFilterDaterangeComponent,
    EidosFilterCheckboxComponent,
    EidosFilterSelectizeComponent,
    EidosFilterNumberComponent,
    EidosFilterTextComponent,
    EidosFilterNumberPopupComponent,

    EidosInputDateTimeComponent,
    EidosInputBooleanComponent,
    EidosInputNumberComponent,
    EidosInputCheckboxComponent,
    EidosInputSwitchComponent,
    EidosInputSelectizeComponent,
    EidosInputDateTimeRangeComponent,
    EidosInputNumberPopupComponent,
    EidosInputPhoneComponent,
    CorePopupListSelectorComponent,
    CorePopupListSelectorModalComponent,
    EidosUploadFileComponent,

    EidosConfigurationViewerComponent,
    EidosConfigurationItemViewerComponent,
    EidosLoginTokenComponent,

    EidosConfirmationDialogComponent,
    EidosAlertDialogComponent,
    EidosErrorDialogComponent,
    EidosChangePasswordDialogComponent,
    EidosSettingsDialogComponent,
    EidosImpersonateUserDialogComponent,
    EidosResetCredentialsDialogComponent,
    EidosFilterCategoryComponent,

    EidosCapsLockDirective,

    CoreTemplateCastPipe,
    CoreFormatCurrencyPipe,
    CoreFormatDateTimePipe,
    CoreSortPipe,
    EidosLightFormComponent,
    EidosGridFullTextTemplateComponent,
    EidosGraphicChooserComponent,
    EidosCommandPanelComponent,
    EidosDragDropDirective,
    EidosModalComponent,
    EidosModalTitleComponent,
    EidosButtonComponent,
    EidosDateSelectorGridComponent,
    EidosTargetValueDirective,
    EidosExpandableTextComponent,
    EidosInputEditorComponent,
    ToJSDatePipe,
    FormatDurationPipe,
    CoreFormatDayOfWeekPipe,
    CoreFormatPercentualNumberPipe,
    CoreFormatTruncatePipe,
    CoreFormatArrayJoinPipe,
    CoreModalContainerComponent,
    CoreModalItemComponent,
    CoreFormModalComponent,
    CoreGridModalComponent,
    CoreTextModalComponent,
    CoreTemplateModalComponent,
    EidosCountryStateCountyAddressComponent,
    CoreIframeComponent,
  ],
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    CdkModule,
    DevExpressModule,
    CoreSafeHtmlPipe,
    AngularEditorModule,
    TooltipDrawerDirective,
    LbaActionMenuComponent,
    EidosAddressAutocompleteComponent
],
  exports: [
    EidosMainPageComponent,
    EidosTopbarComponent,
    EidosExternalAppContainerComponent,
    EidosLoginComponent,
    EidosResetCredentialsComponent,
    EidosHomePageComponent,
    EidosIconComponent,
    EidosProgressSpinnerComponent,
    EidosUserComponent,
    EidosUserAvatarComponent,
    EidosActionMenuComponent,

    EidosGridComponent,
    EidosGridLinkCellTemplateComponent,
    EidosGridFullTextTemplateComponent,
    EidosMatrixComponent,
    EidosLightFormComponent,
    EidosDateSelectorGridComponent,
    EidosInputEditorComponent,

    EidosFilterFormComponent,
    EidosFilterFormCommandsComponent,
    EidosFilterFormShowMoreComponent,
    EidosFilterComponent,
    EidosFilterDaterangeComponent,
    EidosFilterNumberPopupComponent,
    EidosFilterCheckboxComponent,
    EidosFilterSelectizeComponent,

    EidosInputDateTimeComponent,
    EidosInputBooleanComponent,
    EidosInputNumberComponent,
    EidosInputNumberPopupComponent,
    EidosInputCheckboxComponent,
    EidosInputSwitchComponent,
    EidosInputSelectizeComponent,
    EidosInputDateTimeRangeComponent,
    EidosInputPhoneComponent,
    EidosUploadFileComponent,

    EidosConfirmationDialogComponent,
    EidosAlertDialogComponent,
    EidosErrorDialogComponent,

    EidosCapsLockDirective,

    CoreTemplateCastPipe,
    CoreFormatCurrencyPipe,
    CoreFormatDateTimePipe,
    CoreSortPipe,
    EidosGraphicChooserComponent,
    EidosCommandPanelComponent,
    EidosDragDropDirective,
    EidosModalComponent,
    EidosModalTitleComponent,
    EidosTargetValueDirective,
    EidosExpandableTextComponent,
    CorePopupListSelectorComponent,
    ToJSDatePipe,
    FormatDurationPipe,
    CoreFormatDayOfWeekPipe,
    CoreFormatPercentualNumberPipe,
    CoreSafeHtmlPipe,
    CoreFormatTruncatePipe,
    CoreFormatArrayJoinPipe,
    CoreFormModalComponent,
    CoreGridModalComponent
  ],
  providers: [{ provide: ErrorHandler, useClass: CoreErrorHandler }],
})
export class CoreModule { }
